/*
 * Lautio board in real 3D.
 *
 * The board is the actual digital twin - the same mesh and the same
 * photographic maps the rendered chapters use, exported to glTF - lit by an
 * image-based studio environment and tone mapped the way the renders are. It is
 * not a turntable of pictures: the reader turns the real object.
 *
 * Two ways in, and neither of them takes the page away from the reader:
 *   - scrolling past the section turns the board slowly, so it is alive before
 *     anyone touches it, and the page scrolls exactly as it always did;
 *   - dragging it hands over full control, and the wheel only zooms once the
 *     reader has actually engaged with the viewer.
 * Nothing here intercepts the wheel or moves the page itself.
 *
 * Everything - the library, the model - is fetched only when the section comes
 * into view, so a reader who never reaches it pays nothing.
 */
(() => {
  'use strict';

  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-lautio-lib="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') resolve();
      else existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = false;                  // the loaders need THREE to exist first
    s.dataset.lautioLib = src;
    s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve(); });
    s.addEventListener('error', reject);
    document.head.appendChild(s);
  });

  class BoardViewer {
    constructor(root) {
      this.root = root;
      this.mount = root.querySelector('[data-lautio-3d-mount]');
      this.status = root.querySelector('[data-lautio-3d-status]');
      this.hint = root.querySelector('[data-lautio-3d-hint]');
      this.libs = JSON.parse(root.dataset.libs || '[]');
      this.modelUrl = root.dataset.model;
      // one model per size; the switcher loads the one asked for and disposes
      // the one before it, so only one set of textures is ever on the GPU
      this.models = JSON.parse(root.dataset.models || '{}');
      this.size = root.dataset.size || 'large';
      if (!this.modelUrl && this.models[this.size]) this.modelUrl = this.models[this.size];
      this.compact = root.hasAttribute('data-compact');
      this.blobs = {};
      this.visible = true;
      this.exposure = Number(root.dataset.exposure || 0.68);
      this.spin = Number(root.dataset.scrollSpin || 0.4);   // turns per screen
      this.active = false;
      this.booted = false;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
    }

    // A dark studio: black walls, one large soft panel and two smaller ones.
    // Built as a tiny scene and convolved into an environment map, so the board
    // gets real broad reflections without shipping an HDRI. Three's own
    // RoomEnvironment is a bright white room, and against this section's
    // near-black stage it washed the board out completely.
    studioEnvironment(THREE) {
      const room = new THREE.Scene();
      const box = new THREE.BoxGeometry();
      box.deleteAttribute('uv');
      const shell = new THREE.Mesh(box, new THREE.MeshStandardMaterial({
        side: THREE.BackSide, color: 0x16241c, roughness: 1, metalness: 0,
      }));
      shell.scale.set(12, 8, 12);
      room.add(shell);
      // Warm light, not white: the panels carry a little of the daylight tint
      // the graded chapters have, so the model sits in the same world.
      const panel = (w, h, d, x, y, z, intensity, warm = 1.0) => {
        const m = new THREE.Mesh(box, new THREE.MeshBasicMaterial());
        m.material.color.setRGB(intensity * warm, intensity * (0.97 + 0.03 / warm), intensity * (0.90 + 0.10 / warm));
        m.scale.set(w, h, d);
        m.position.set(x, y, z);
        room.add(m);
      };
      panel(7.0, 0.1, 5.0, -1.6, 3.4, -1.2, 2.9, 1.04);   // the key, overhead and left, warm
      panel(0.1, 3.2, 3.6, 5.2, 1.4, 1.0, 1.15, 0.98);     // a soft fill from the right
      panel(3.0, 2.0, 0.1, -0.6, 1.0, 5.4, 0.7, 0.99);   // a wall behind the camera
      // A floor panel, because the underside of the board has to be a real
      // surface too. With only overhead sources there was nothing below the
      // board for it to reflect, so turning it over showed a black plate.
      panel(9.0, 0.1, 9.0, 0.0, -2.6, 0.0, 0.74, 1.02);
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      pmrem.compileEquirectangularShader();
      const tex = pmrem.fromScene(room, 0.03).texture;
      pmrem.dispose();
      return tex;
    }

    observe() {
      if (!('IntersectionObserver' in window)) { this.boot(); return; }
      // Building a viewer - evaluating three.js, a WebGL context, the studio
      // environment, the model, the first shader compile - is a quarter of a
      // second of main thread. It used to start 300 px before the section came
      // into view, which is to say in the middle of the reader's scroll through
      // the chapter above: a 200 ms hitch, measured at 0.86 of that chapter.
      // Now it is armed a screen out and runs at the first pause
      // in the scrolling, in steps with a frame between them; 300 px stays as
      // the last resort for a reader who never pauses.
      const near = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { near.disconnect(); this.bootWhenStill(); }
      }, { rootMargin: '100% 0px' });
      near.observe(this.root);
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { io.disconnect(); this.forced = true; this.boot(); }
        }
      }, { rootMargin: '300px 0px' });
      io.observe(this.root);
    }

    static still() {
      if (!BoardViewer.lastScroll) {
        BoardViewer.lastScroll = performance.now();
        window.addEventListener('scroll', () => { BoardViewer.lastScroll = performance.now(); }, { passive: true });
      }
      return performance.now() - BoardViewer.lastScroll > 320;
    }

    bootWhenStill() {
      if (this.booted) return;
      const look = () => {
        if (this.booted) return;
        if (BoardViewer.still()) { this.boot(); return; }
        setTimeout(look, 160);
      };
      look();
    }

    // Between two steps of the build: a presented frame, and - unless the
    // section is about to come into view - the reader's next pause. A build
    // begun at a pause must not carry on through the scroll that follows.
    breathe(step) {
      if (this.stepAt !== undefined) {
        try { performance.measure(`lautio:3d-${this.stepName}`, { start: this.stepAt, end: performance.now() }); } catch (e) { /* no measure */ }
      }
      return new Promise((resolve) => {
        const go = () => requestAnimationFrame(() => setTimeout(() => { this.stepName = step; this.stepAt = performance.now(); resolve(); }, 0));
        const wait = () => { if (this.forced || BoardViewer.still()) go(); else setTimeout(wait, 120); };
        wait();
      });
    }

    waitForBox() {
      return new Promise((resolve) => {
        let tries = 0;
        const look = () => {
          // the mount is absolutely positioned inside the stage, so measure the
          // stage: an empty mount can report 0 x 0 while the stage is fully laid out
          const box = this.mount.parentElement || this.mount;
          if ((box.clientWidth > 2 && box.clientHeight > 2) || tries > 40) {
            resolve();
            return;
          }
          tries += 1;
          requestAnimationFrame(look);
        };
        look();
      });
    }

    fail(message) {
      this.root.classList.add('is-failed');
      if (this.status) { this.status.hidden = false; this.status.textContent = message; }
    }

    async boot() {
      if (this.booted) return;
      this.booted = true;
      const canvasProbe = document.createElement('canvas');
      const hasWebGL = !!(window.WebGLRenderingContext
        && (canvasProbe.getContext('webgl') || canvasProbe.getContext('experimental-webgl')));
      if (!hasWebGL) { this.fail('Your browser cannot show the 3D model'); return; }
      try {
        await this.breathe('libs');
        for (const src of this.libs) { await loadScript(src); await this.breathe('libs'); }
        // one viewer builds at a time: four of them parsing models and baking
        // environments together is what stalls a phone
        const turn = BoardViewer.chain.then(() => this.build());
        BoardViewer.chain = turn.catch(() => {});
        await turn;
      } catch (err) {
        // kept on the instance so a failure can be diagnosed without shipping
        // console noise to every reader
        this.error = err;
        this.fail('Could not load the 3D model');
      }
    }

    async build() {
      const THREE = window.THREE;
      // The model first: parsing it is the longest single step (58 ms for the
      // desktop model) and needs no WebGL, so it is the one most worth having
      // behind us before the section forces the rest.
      this.stepName = 'model'; this.stepAt = performance.now();
      const gltf = await this.loadGltf(this.modelUrl);
      this.board = gltf.scene;
      await this.breathe('renderer');
      // The section is built before it is on screen, so the stage can still be
      // unmeasured at this point - on the real store the canvas came out 1 x 1.
      // Wait for a real box, then keep watching it: a window resize is not the
      // only thing that changes an element's size.
      await this.waitForBox();
      const stage = this.mount.parentElement || this.mount;
      const w = this.mount.clientWidth || stage.clientWidth || 1;
      const h = this.mount.clientHeight || stage.clientHeight || 1;

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: this.compact ? 'low-power' : 'high-performance' });
      // the card canvases are small, so the full 2x costs little and keeps the
      // boards in the shop as sharp as the one in the big viewer
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.setSize(w, h, false);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = this.exposure;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.mount.appendChild(this.renderer.domElement);
      await this.breathe('environment');
      // Four viewers on one page: a browser under pressure can take a context
      // away and hand it back. Losing one must not leave a white rectangle -
      // the loop stops, the poster shows through, and drawing resumes when
      // the context is restored (Three re-initialises its state itself).
      this.renderer.domElement.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
        this.root.classList.remove('is-ready');
      });
      this.renderer.domElement.addEventListener('webglcontextrestored', () => {
        this.root.classList.add('is-ready');
        if (this.controls && !this.raf) this.raf = requestAnimationFrame(this.tick);
      });

      this.scene = new THREE.Scene();

      // An image-based environment rather than a couple of point lights: wood at
      // a grazing angle is mostly a broad specular sheen, and that only exists
      // if there is something around the object for it to reflect.
      //
      // Three's RoomEnvironment is a bright white room, and against the section's
      // near-black stage it washed the board out completely. This is a dark
      // studio instead: black walls with a few soft panels, which is what the
      // rendered chapters are lit by too.
      this.scene.environment = this.studioEnvironment(THREE);
      await this.breathe('prepare');

      const key = new THREE.DirectionalLight(0xfff6ec, 1.45);
      key.position.set(-0.55, 0.95, 0.45);
      key.castShadow = true;
      key.shadow.mapSize.set(this.compact ? 1024 : 2048, this.compact ? 1024 : 2048);
      key.shadow.bias = -0.0006;
      key.shadow.normalBias = 0.004;
      const cam = key.shadow.camera;
      cam.left = -0.5; cam.right = 0.5; cam.top = 0.5; cam.bottom = -0.5;
      cam.near = 0.05; cam.far = 3.0;
      this.scene.add(key);
      this.scene.add(new THREE.DirectionalLight(0xdfe8ff, 0.22).translateX(0.8));
      // and a soft bounce from underneath, the way a pale surface under a board
      // throws light back at it
      const under = new THREE.DirectionalLight(0xfff3e6, 0.55);
      under.position.set(0.25, -1.0, 0.4);
      this.scene.add(under);

      this.camera = new THREE.PerspectiveCamera(32, w / h, 0.01, 20);

      this.prepareBoard(THREE, this.board);
      await this.breathe('scene');
      this.wireSwitcher();
      this.watchVisibility();
      // Centre the model on its own bounding box and frame it from its size, so
      // the viewer does not depend on where the exporter happened to put it.
      const box = new THREE.Box3().setFromObject(this.board);
      const size = box.getSize(new THREE.Vector3());
      const centre = box.getCenter(new THREE.Vector3());
      this.board.position.sub(centre);
      this.pivot = new THREE.Group();
      this.pivot.add(this.board);
      this.scene.add(this.pivot);

      // Frame the board from what the camera actually sees, not from a sphere
      // drawn around it. Fitting the bounding sphere is safe but leaves a flat
      // plate marooned in the middle of the stage; the 0.82 that used to
      // compensate for that pulled the camera in far enough to cut the board
      // off on both sides wherever the stage is close to square - which is
      // every phone. distanceFor solves the distance from the projection
      // itself, so the whole silhouette fits whatever the model measures and
      // however wide the screen is. The board keeps its proportions; only the
      // camera moves.
      const radius = size.length() / 2;
      this.frameRadius = radius;
      this.frameHalf = size.clone().multiplyScalar(0.5);
      // how much of the stage the widest turn of the board is allowed to fill
      this.fitFill = this.compact ? 0.86 : 0.94;
      this.fitDistance = (aspect, from) => this.distanceFor(aspect, from);
      const rest = new THREE.Vector3(0.62, 0.52, 0.86).normalize();
      const restDistance = this.fitDistance(w / h, rest);
      this.camera.position.copy(rest.multiplyScalar(restDistance));
      this.camera.lookAt(0, 0, 0);

      const shadowPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(radius * 8, radius * 8),
        new THREE.ShadowMaterial({ opacity: 0.42 }),
      );
      shadowPlane.rotation.x = -Math.PI / 2;
      // just under the board: the board never tips - the camera orbits it - so
      // a plane a few millimetres below always reads as contact. At 90 mm down
      // the shadow separated and looked like a second object.
      shadowPlane.position.y = -size.y / 2 - radius * 0.012;
      shadowPlane.receiveShadow = true;
      this.scene.add(shadowPlane);

      this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      // slower and heavier: Daniel asked for the board to turn more calmly, and
      // a lighter damping factor makes the object feel like it has mass
      this.controls.dampingFactor = 0.045;
      this.controls.enablePan = false;
      this.controls.enableZoom = false;          // until the reader engages
      this.controls.minDistance = radius * 1.5;
      this.controls.maxDistance = Math.max(radius * 6.0, restDistance * 1.4);
      this.controls.minPolarAngle = 0.15;
      this.controls.maxPolarAngle = Math.PI - 0.15;
      this.controls.rotateSpeed = 0.55;

      const engage = () => {
        if (this.active) return;
        this.active = true;
        this.controls.enableZoom = true;
        this.root.classList.add('is-active');
      };
      this.renderer.domElement.addEventListener('pointerdown', engage);
      this.renderer.domElement.addEventListener('keydown', engage);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.onResize());
        this.ro.observe(this.mount);
      }
      this.onScroll();
      this.spinTarget = this.spinCurrent;
      // the first render compiles the shaders and uploads the textures: paid
      // here, at the pause the build was started in, not on the frame the
      // section scrolls into view
      // The shaders one mesh at a time, then the textures one at a time, a
      // frame between each: the first render used to compile and upload the
      // lot in one go, 170 ms of it.
      await this.breathe('first-render');
      const meshes = [];
      this.scene.traverse((o) => { if (o.isMesh) meshes.push(o); });
      const shown = meshes.map((m) => m.visible);
      meshes.forEach((m) => { m.visible = false; });
      for (let i = 0; i < meshes.length; i += 1) {
        meshes[i].visible = true;
        this.renderer.compile(this.scene, this.camera);
        meshes[i].visible = false;
        await this.breathe('first-render');
      }
      meshes.forEach((m, i) => { m.visible = shown[i]; });
      if (this.renderer.initTexture) {
        const seen = new Set();
        for (const m of meshes) {
          const mat = m.material;
          if (!mat) continue;
          for (const slot of ['map', 'roughnessMap', 'metalnessMap', 'normalMap', 'aoMap']) {
            const tex = mat[slot];
            if (!tex || seen.has(tex)) continue;
            seen.add(tex);
            this.renderer.initTexture(tex);
            await this.breathe('first-render');
          }
        }
      }
      this.renderer.render(this.scene, this.camera);
      await this.breathe('ready');
      this.root.classList.add('is-ready');
      if (this.status) this.status.hidden = true;
      this.raf = requestAnimationFrame(this.tick);
    }


    prepareBoard(THREE, board) {
      // Anisotropic filtering is the single biggest quality win here. The board
      // is almost always seen at a grazing angle, and without it the maps are
      // sampled along one axis only, which is what made the grain look soft and
      // shimmer while turning.
      const maxAniso = this.renderer.capabilities.getMaxAnisotropy();
      board.traverse((o) => {
        if (!o.isMesh) return;
        o.castShadow = true;
        o.receiveShadow = true;
        const m = o.material;
        if (!m) return;
        m.envMapIntensity = 0.72;
        for (const slot of ['map', 'roughnessMap', 'metalnessMap', 'normalMap', 'aoMap']) {
          const tex = m[slot];
          if (!tex) continue;
          tex.anisotropy = maxAniso;
          tex.generateMipmaps = true;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.magFilter = THREE.LinearFilter;
          tex.needsUpdate = true;
        }
        m.needsUpdate = true;
      });

    }

    async loadGltf(url) {
      const THREE = window.THREE;
      if (!this.blobs[url]) {
        const res = await fetch(url, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`${res.status} ${url}`);
        this.blobs[url] = await res.arrayBuffer();
      }
      return new Promise((res, rej) => new THREE.GLTFLoader().parse(this.blobs[url], '', res, rej));
    }

    // The size switcher: Small, Medium, Large. The board in view is replaced,
    // its textures freed, and the camera re-framed for the new length. The
    // commerce block is told too, so the two choices stay one choice.
    wireSwitcher() {
      const buttons = [...this.root.querySelectorAll('[data-lautio-3d-size]')];
      if (!buttons.length) return;
      // data-lautio-3d-size does not camel-case cleanly (the digit), so read the attribute
      const sizeOf = (b) => b.getAttribute('data-lautio-3d-size');
      const reflect = (size) => buttons.forEach((b) => b.setAttribute('aria-pressed', String(sizeOf(b) === size)));
      reflect(this.size);
      buttons.forEach((b) => b.addEventListener('click', () => {
        const size = sizeOf(b);
        if (size === this.size) return;
        reflect(size);
        this.setSize(size);
        const el = document.querySelector('lautio-capture-locked');
        if (el && typeof el.selectSize === 'function') el.selectSize(size);
      }));
    }

    async setSize(size) {
      const THREE = window.THREE;
      const url = this.models[size];
      if (!url || this.switching) return;
      this.switching = true;
      try {
        const gltf = await this.loadGltf(url);
        const next = gltf.scene;
        this.prepareBoard(THREE, next);
        const old = this.board;
        // frame the new length
        const box = new THREE.Box3().setFromObject(next);
        const sz = box.getSize(new THREE.Vector3());
        next.position.sub(box.getCenter(new THREE.Vector3()));
        this.pivot.add(next);
        this.board = next;
        this.size = size;
        this.root.dataset.size = size;
        const radius = sz.length() / 2;
        this.frameRadius = radius;
        this.frameHalf = sz.clone().multiplyScalar(0.5);
        this.controls.minDistance = radius * 1.5;
        this.controls.maxDistance = Math.max(radius * 6.0, this.camera.position.length() * 1.6);
        this.refit();
        // a soft scale-in for the newcomer while the old one goes
        next.scale.setScalar(0.94);
        const t0 = performance.now();
        const grow = () => {
          const k = Math.min(1, (performance.now() - t0) / 420);
          const e = 1 - Math.pow(1 - k, 3);
          next.scale.setScalar(0.94 + 0.06 * e);
          if (k < 1) requestAnimationFrame(grow);
        };
        grow();
        if (old) {
          this.pivot.remove(old);
          old.traverse((o) => {
            if (!o.isMesh) return;
            if (o.geometry) o.geometry.dispose();
            const m = o.material;
            if (!m) return;
            for (const slot of ['map', 'roughnessMap', 'metalnessMap', 'normalMap', 'aoMap']) if (m[slot]) m[slot].dispose();
            m.dispose();
          });
        }
      } finally {
        this.switching = false;
      }
    }

    // The camera always looks at the origin, so its own axes depend only on the
    // direction it sits in and not on how far away it is. That turns the
    // framing into a closed form rather than a search: a corner p is inside the
    // frustum when |p.right| <= (d - p.view) * tanH and |p.up| <= (d - p.view)
    // * tanV, so the distance the whole board needs is the largest of those
    // over its eight corners - taken over a full turn, so the framing does not
    // breathe while the reader scrolls the board around.
    distanceFor(aspect, from) {
      const THREE = window.THREE;
      const half = this.frameHalf;
      if (!THREE || !half || !this.camera) return this.frameRadius * 3;
      const view = (from && from.lengthSq() > 1e-6 ? from.clone() : new THREE.Vector3(0.62, 0.52, 0.86)).normalize();
      const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), view);
      if (right.lengthSq() < 1e-8) right.set(1, 0, 0);
      right.normalize();
      const up = new THREE.Vector3().crossVectors(view, right).normalize();
      const tanV = Math.tan((this.camera.fov * Math.PI) / 360) * this.fitFill;
      const tanH = tanV * Math.max(aspect, 0.2);
      const p = new THREE.Vector3();
      const turns = 48;
      let d = 0;
      for (let i = 0; i < turns; i += 1) {
        const a = (i / turns) * Math.PI * 2;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        for (let c = 0; c < 8; c += 1) {
          const x = c & 1 ? half.x : -half.x;
          const y = c & 2 ? half.y : -half.y;
          const z = c & 4 ? half.z : -half.z;
          p.set(x * cos + z * sin, y, z * cos - x * sin);
          const along = p.dot(view);
          d = Math.max(d, along + Math.abs(p.dot(right)) / tanH, along + Math.abs(p.dot(up)) / tanV);
        }
      }
      return d;
    }

    refit() {
      if (!this.fitDistance || !this.camera) return;
      const w = this.mount.clientWidth || 1;
      const h = this.mount.clientHeight || 1;
      const dir = this.camera.position.clone().normalize();
      const d = this.fitDistance(w / h, dir);
      this.camera.position.copy(dir.multiplyScalar(d));
      this.camera.lookAt(0, 0, 0);
      if (this.controls) this.controls.update();
    }

    // The render loop runs only while the stage is on screen. Four of these on
    // one page, all rendering off-screen, is what made the page feel heavy.
    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          this.visible = e.isIntersecting;
          if (this.visible && !this.raf && this.controls) this.raf = requestAnimationFrame(this.tick);
        }
      }, { rootMargin: '120px 0px' });
      io.observe(this.root);
    }

    // How far the section has travelled through the viewport, 0 to 1. This only
    // reads the scroll position; it never writes it.
    onScroll() {
      const r = this.root.getBoundingClientRect();
      const travel = r.height + window.innerHeight;
      const p = clamp((window.innerHeight - r.top) / travel, 0, 1);
      this.spinTarget = (p - 0.5) * this.spin * Math.PI * 2;
      if (this.spinCurrent === undefined) this.spinCurrent = this.spinTarget;
    }

    onResize() {
      if (!this.renderer) return;
      const stage = this.mount.parentElement || this.mount;
      const w = this.mount.clientWidth || stage.clientWidth || 1;
      const h = this.mount.clientHeight || stage.clientHeight || 1;
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      // a narrow stage needs the camera further back for the board to fit
      if (this.fitDistance && !this.active) {
        this.camera.position.setLength(this.fitDistance(w / h, this.camera.position.clone().normalize()));
      }
    }

    tick() {
      if (!this.visible) { this.raf = 0; return; }
      this.raf = requestAnimationFrame(this.tick);
      // Once the reader has taken hold of the board, the scroll stops turning it
      // - two things driving one rotation feels broken.
      if (!this.active) {
        const d = this.spinTarget - this.spinCurrent;
        this.spinCurrent += d * (this.reduced ? 1 : 0.035);
        this.pivot.rotation.y = this.spinCurrent;
      }
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    }
  }

  BoardViewer.chain = Promise.resolve();

  const boot = () => {
    document.querySelectorAll('[data-lautio-3d]').forEach((el) => {
      if (el.dataset.lautioBooted) return;
      el.dataset.lautioBooted = '1';
      const v = new BoardViewer(el);
      el.__viewer = v;
      v.observe();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
